import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ChartBar } from "@phosphor-icons/react";

/**
 * Iter118 — Statistiche Prezzo Flessibile.
 *
 * Chiama GET /services/{id}/pricing-stats e mostra:
 *  - KPI riepilogo (pratiche totali, prezzo medio)
 *  - Top combinazioni (sequenza risposte più scelta)
 *  - Distribuzione risposte per domanda con barre orizzontali
 */
export function PricingStatsDialog({ open, onClose, service }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !service) return;
    setStats(null);
    setError(null);
    setLoading(true);
    api.get(`/services/${service._id}/pricing-stats`)
      .then(r => setStats(r.data))
      .catch(e => setError(e.response?.data?.detail || "Errore caricamento"))
      .finally(() => setLoading(false));
  }, [open, service]);

  const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pricing-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChartBar size={18} weight="fill" /> Statistiche Prezzo Flessibile
          </DialogTitle>
          <DialogDescription>
            {service?.name || ""} — Distribuzione delle risposte scelte dai clienti
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-sm text-slate-500 text-center py-8">Caricamento…</div>
        )}
        {error && (
          <div className="text-sm text-red-600 py-4">{error}</div>
        )}
        {stats && (
          <div className="space-y-6">
            {/* KPI Riepilogo */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="stats-total-practices">
                <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold">
                  Pratiche configurate
                </div>
                <div className="text-2xl font-bold text-indigo-900 mt-1">
                  {stats.total_practices}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="stats-avg-price">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                  Prezzo medio finale
                </div>
                <div className="text-2xl font-bold text-emerald-900 mt-1">
                  {fmt(stats.avg_adjusted_price_eur)}
                </div>
              </div>
            </div>

            {stats.total_practices === 0 ? (
              <div className="text-sm text-slate-500 italic p-6 text-center border border-dashed rounded">
                Nessuna pratica con Prezzo Flessibile configurato per questo servizio.
              </div>
            ) : (
              <>
                {/* Top combinazioni */}
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-slate-800">
                    🏆 Top combinazioni scelte
                  </h3>
                  <div className="space-y-2">
                    {stats.top_combinations.map((c, idx) => (
                      <div
                        key={idx}
                        className="p-2 rounded border border-slate-200 bg-white"
                        data-testid={`stats-combo-${idx}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-800 text-white text-[10px] font-bold">
                              #{idx + 1}
                            </span>
                            <span className="text-xs font-semibold text-slate-700">
                              {c.count} pratiche ({c.percentage}%)
                            </span>
                          </div>
                          <span className="text-xs text-emerald-700 font-semibold">
                            {fmt(c.avg_price_eur)} medio
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 leading-relaxed pl-8">
                          {c.labels.map((l, i) => (
                            <span key={i}>
                              {l}
                              {i < c.labels.length - 1 && <span className="text-slate-400"> → </span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Distribuzione per domanda */}
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-slate-800">
                    📊 Distribuzione risposte per domanda
                  </h3>
                  <div className="space-y-3">
                    {stats.by_question.map((q, qidx) => (
                      <div
                        key={q.question_id}
                        className="p-2 rounded border border-slate-200 bg-white"
                        data-testid={`stats-question-${qidx}`}
                      >
                        <div className="text-xs font-semibold text-slate-800 mb-2">
                          {q.question_label}{" "}
                          <span className="text-slate-500 font-normal">({q.total} risposte)</span>
                        </div>
                        <div className="space-y-1.5">
                          {q.answers.map((a, aidx) => (
                            <div key={a.answer_id} className="flex items-center gap-2" data-testid={`stats-answer-${qidx}-${aidx}`}>
                              <div className="text-xs w-32 truncate text-slate-700">
                                {a.answer_label}
                              </div>
                              <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden relative">
                                <div
                                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                                  style={{ width: `${a.percentage}%` }}
                                />
                              </div>
                              <div className="text-xs font-mono text-slate-600 w-20 text-right shrink-0">
                                {a.count} · {a.percentage}%
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PricingStatsDialog;
